import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { formatDateOnly } from '../../../../lib/formatDate';
import { personnelApi } from '../personnelApi';
import type { PersonnelServiceRecord } from '../personnelTypes';

/**
 * Ends a line of service, vacating the item.
 *
 * **The row is closed, never deleted** — a post keeps the record of who held it and when,
 * which is the whole reason service records are a history rather than a pointer on the item. So
 * this is not a delete dialog and does not use one: nothing is destroyed, so there is no
 * ceremony to justify beyond the standard confirm.
 */
export function EndServiceRecordModal({
  personnelId,
  serviceRecord,
  onClose,
  onSaved,
}: {
  personnelId: string;
  serviceRecord: PersonnelServiceRecord;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [effectiveTo, setEffectiveTo] = useState('');
  const [remarks, setRemarks] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    Checked here so the reader is told before submitting, and on the server so it holds for
    any caller. The client copy is the courtesy; the server's is the rule.
  */
  const endsBeforeItBegan = effectiveTo !== '' && effectiveTo < serviceRecord.effectiveFrom;
  const valid = effectiveTo.length > 0 && !endsBeforeItBegan;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await personnelApi.endServiceRecord(personnelId, serviceRecord.id, {
        effectiveTo,
        remarks: remarks.trim() || null,
      });

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not end the service record.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'end-service-record' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="End service record"
      size="lg"
      id="end-service-record-modal"
      /*
        Today is the only honest default for a last day held: the server refuses a date before
        the appointment began, and any other guess would be a claim about when someone left.
      */
      autogenerate={{
        onGenerate: () => {
          setEffectiveTo(todayCalendarDate());
          setRemarks('Sample end of appointment.');
        },
        disabled: saving,
      }}
      confirm={{
        id: 'end-service-record-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="end-service-record-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            id="end-service-record-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          The consequence, stated before the tick rather than appended to it: the item becomes
          vacant, and the record of this appointment stays. A reader ending an appointment may
          reasonably expect the row to disappear, and it does not.
        */}
        <p className="text-sm text-slate-600">
          <span className="font-mono">{serviceRecord.itemNumber}</span> — {serviceRecord.positionTitle},{' '}
          {serviceRecord.orgUnitName}. Held since {formatDateOnly(serviceRecord.effectiveFrom)}.
        </p>
        <p className="text-xs text-slate-500">
          The item becomes vacant and can be filled by someone else. This appointment is kept as
          part of the post&apos;s history.
        </p>

        <TextField
          id="end-service-record-effective-to"
          label="Last day held"
          type="date"
          required
          value={effectiveTo}
          onChange={(e) => setEffectiveTo(e.target.value)}
          error={
            endsBeforeItBegan
              ? `Choose a date on or after ${formatDateOnly(serviceRecord.effectiveFrom)}.`
              : effectiveTo
                ? error?.fieldError('effectiveTo')
                : 'Choose the last day held.'
          }
          disabled={saving}
        />

        <TextField
          id="end-service-record-remarks"
          label="Remarks"
          description="Why the appointment ended, when it needs saying."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          error={error?.fieldError('remarks')}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
